import { LangLocale, languages } from '@myre/common-i18n';
import {
  LANGLOCALE,
  SafeUrlPipe,
  TranslatePipe,
} from '@myre/ui-primitive-core';

import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Input,
} from '@angular/core';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';

import { getTypeFromExtension } from './doc-viewer.helper';

// Replaced at build time by the app builder's `define` (see pipeline/sync
// sync-configs) with the version-stamped folder the bundled pdf.js viewer is
// copied to, e.g. `assets/pdfjs-26.0.1`. Undefined under tests/typecheck, where
// the viewer is never rendered — fall back to the library's own default path.
declare const NG_PDFJS_VIEWER_FOLDER: string | undefined;

const PDFJS_VIEWER_FOLDER =
  typeof NG_PDFJS_VIEWER_FOLDER === 'undefined'
    ? 'assets/pdfjs'
    : NG_PDFJS_VIEWER_FOLDER;

@Component({
  selector: 'myre-doc-viewer',
  templateUrl: './doc-viewer.component.html',
  styleUrls: ['./doc-viewer.component.scss'],
  imports: [PdfJsViewerModule, SafeUrlPipe, TranslatePipe],
})
export class DocViewerComponent {
  @Input()
  public set src(src: string) {
    this._src = src;
    void this.loadPdf();
  }

  public get src(): string {
    return this._src;
  }

  @Input()
  public set extension(extension: string) {
    if (!extension) {
      return;
    }

    this._extension = extension;
    this.type = getTypeFromExtension(extension);
    void this.loadPdf();
  }

  public get extension(): string {
    return this._extension;
  }

  // Opt-in: make the viewer fill the viewport height instead of the default
  // fixed height. Used when the viewer is the sole content of a full modal.
  @HostBinding('class.full-height')
  @Input()
  public fullHeight = false;

  public type: 'img' | 'pdf';
  public locale: string;
  public pdfSrc: Blob;
  public readonly viewerFolder = PDFJS_VIEWER_FOLDER;

  private _src: string;
  private _extension: string;
  private readonly changeDetectionRef = inject(ChangeDetectorRef);

  constructor() {
    const langLocale = inject<LangLocale>(LANGLOCALE);

    this.locale = languages[langLocale].intlLocale;
  }

  private async loadPdf(): Promise<void> {
    if (this.type !== 'pdf' || !this._src) {
      return;
    }

    const response = await fetch(this._src);

    this.pdfSrc = await response.blob();
    this.changeDetectionRef.markForCheck();
  }
}
